import { ReactElement, useEffect, useState } from "react";
import { T } from "react-translator-component";
import { colors } from "../../../common/colors";
import LoadingModal from "../../../components/LoadingModal";
import { formatEventDate } from "../../util/posts";
import PostAttachment from "../post/PostAttachment";
import { useAppDispatch } from "../../../redux/store";
import { setErrorObj } from "../../redux/errorReducer";
import apiServices from "../../services/apiServices";
import DisplayModal from "../DisplayModal";
import SafeTextWithLinks from "../SafeTextWithLinks";
import EventApprovedUsers from "./EventApprovedUsers";

interface Props {
  open: boolean;
  post: any;
  highlightWords?: string[];
  onClose: () => void;
}

type ChildTableData = {
  title: string,
  value: string | number
}

export default function PostEventDetailModal({open, post, highlightWords, onClose}: Props): ReactElement {
  const dispatch = useAppDispatch();

  const [event, setEvent] = useState<any>();
  const [showPostEventUsers, setShowPostEventUsers] = useState<boolean>(false);
  const [postEventUsers, setPostEventUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!open || !post) return

    const apiCalls = async () => {
      setIsLoading(true)
      try {
        const event = (await apiServices.getEventPost(post.id)).post_event
        if (event.post_event_users_count > 0) {
          const res = await apiServices.getEventPostUsers(post.id)
          setPostEventUsers(res.post_event_users)
        }
        setEvent(event)
      } catch (error: any) {
        dispatch(setErrorObj(error))
      } finally {
        setIsLoading(false)
      }
    }
    apiCalls()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, post])

  const renderApplicationSetting = (setting: any, index: number) => {
    const amount = setting.free ? 0 : Number(setting.fee_amount).toLocaleString()
    const childTableData: ChildTableData[] = [
      {title: T("mng_event_application_name"), value: setting.name},
      {title: T("mng_event_application_nop"), value: `${setting.number_of_participants}${T("mng_unit_person")}`},
      {title: T("mng_event_application_amount"), value: `${amount}${T("mng_unit_yen")}`},
    ]

    return (
      <table style={styles.table} key={index}>
        <tbody style={styles.table}>
          <tr style={index === event?.participant_settings.length -1 ? {} : styles.bottomLine}>
            <th style={styles.applicationIndex}>{index + 1}</th>
            {renderChildTable(String(setting.id), childTableData)}
          </tr>
        </tbody>
      </table>
    )
  }

  const renderPostEventVenue = () => {
    return (
      renderCell(
        T("mng_event_venue"),
        (
          <>
            <p style={styles.eventData}>
              {event?.postal_code}
            </p>
            <p style={styles.eventData}>
              {event?.address}
            </p>
            <p style={styles.eventData}>
              {event?.venue_name}
            </p>
          </>
        )
      )
    )
  }

  const renderChildTable = (prefix: string, dataList: ChildTableData[]) => {
    return (
      <td style={{...styles.childCell, borderTop: "none", borderRight: "none", borderBottom: "none"}}>
        <table style={styles.table}>
          <tbody style={styles.table}>
            {dataList.map((data: ChildTableData, index: number) => (
              <tr style={index === dataList.length - 1 ? {} : styles.bottomLine} key={`${prefix}_${index}`}>
                <th style={styles.childCellTitle}>{data.title}</th>
                <td style={styles.childCellData}>{data.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    )
  }

  const renderCell = (title: string, data: any) => {
    return (
      <tr style={styles.bottomLine}>
        <th style={styles.cellTitle}>{title}</th>
        <td style={styles.cell}>
          {data}
        </td>
      </tr>
    )
  }

  const handleShowUsers = async () => {
    setShowPostEventUsers(true)
  }

  const handleCloseUsers = () => {
    setShowPostEventUsers(false)
  }

  const handleClose = () => {
    handleCloseUsers()
    onClose()
    setIsLoading(true) //次回オープン時にモーダルがちらつかないようにする
  }

  return (
    <>
      <LoadingModal visible={isLoading && open} />
      <DisplayModal
        open={!isLoading && open}
        title={event?.title}
        buttonLabel={showPostEventUsers ? T("mng_back") : undefined}
        secondButton={
          showPostEventUsers ? undefined : {
            label: T("mng_event_participation_status"),
            disabled: event?.participant_settings.length === 0 || event?.post_event_users_count === 0,
            onClick: handleShowUsers
          }
        }
        sx={styles.container}
        highlightWords={highlightWords}
        onClose={() => showPostEventUsers ? handleCloseUsers() : handleClose()}
        onModalClose={handleClose}
      >
        {showPostEventUsers ? (
          <EventApprovedUsers postEventUsers={postEventUsers} />
        ) : event ? (
          <div style={styles.tableContainer}>
            <table style={styles.table} border={1}>
              <tbody>
                {renderCell(
                  T("mng_event_date"),
                  formatEventDate(event.start_date, event.end_date)
                )}

                {renderPostEventVenue()}

              {renderCell(
                T("mng_event_url"),
                (<SafeTextWithLinks text={event?.url} highlightWords={highlightWords} />)
              )}

                {renderCell(
                  T("mng_post_attachments"),
                  (event.attachments?.map((attachment: any, index: number) => (
                    <div key={index}>
                      <PostAttachment
                        post_id={post.id}
                        attachment={attachment}
                      />
                    </div>
                  )))
                )}

                {renderCell(
                  T("mng_event_application_term"),
                  formatEventDate(
                    event.application_start_date,
                    event.application_end_date
                  )
                )}

                <tr style={styles.bottomLine}>
                  <th style={styles.cellTitle}>{T("mng_event_applications")}</th>
                  <td style={{...styles.cell, padding: 0}}>
                    {event.participant_settings?.map((item: any, index: any) => (
                      renderApplicationSetting(item, index)
                    ))}
                  </td>
                </tr>

                {renderCell(
                  T("mng_event_can_participate"),
                  event.member_participation_rule
                )}

                {renderCell(
                  T("mng_event_about_cancel"),
                  event.application_cancellation_rule
                )}

                {renderCell(
                  T("mng_event_how_payment"),
                  T("mng_mebuku_pay")
                )}

                {event.participant_settings.length > 0 &&
                  renderCell(
                    T("mng_event_application_users_count"),
                    `${event.post_event_users_count || 0}${T("mng_unit_person")}`
                  )
                }
              </tbody>
            </table>
          </div>
        ) : <></>}
      </DisplayModal>
    </>
  );
}

const styles = {
  tableContainer: {
    maxHeight: "calc(100vh*0.9*0.7 - 90px)",
    overflowY: "auto" as const,
    padding: "0 15px",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse" as const
  },

  cell: {
    padding: 10,
    border: "1px solid black",
    textAlign: "left" as const
  },

  childCell: {
    padding: 0,
    border: "1px solid black",
    textAlign: "left" as const,
    width: "100%"
  },

  cellTitle: {
    width: "180px",
  },

  childCellTitle: {
    fontWeight: "normal",
    width: "100px",
    paddingLeft: 10,
  },

  childCellData: {
    padding: 10,
    borderLeft: `1px solid ${colors.farm_black}`,
    overflowWarp: "anywhere",
    lineBreak: "anywhere" as const,
  },

  container: {
    minWidth: "45%",
    maxWidth: "85%",
    paddingX: 0,
    paddingBottom: 0,
    textAlign: "center"
  },

  bottomLine: {
    borderBottom: `1px solid ${colors.farm_black}`,
  },

  eventData: {
    fontSize: 16,
    lineHeight: "20px",
    margin: 0
  },

  linkText: {
    userSelect: "text" as "text",
    fontSize: 16,
    fontFamily: "mplus-1c-regular",
    wordWrap: "break-word" as "break-word",
  },
  applicationIndex: {
    padding: "0 8px",
    textAlign: "center" as const,
  }
}